/**
 * Extracts performance metrics from audit trail entries.
 *
 * Computes total duration, token counts (input/output), and rule outcome
 * breakdowns by parsing RULE_EXECUTION and EXTERNAL_SERVICE_CALL entries.
 */

import type { AuditEntry } from '@amzn/helianthus-arbiter-client';
import { AuditEntryType } from '@amzn/helianthus-arbiter-client';
import { parseRuleExecutionEntry, parseExternalServiceEntry } from './auditEntryParsers';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuditTrailMetrics {
  /** Total wall-clock duration in milliseconds (first to last entry timestamp) */
  totalDurationMs: number | null;
  /** Total input tokens across all entries carrying LLM token counts */
  totalInputTokens: number;
  /** Total output tokens across all entries carrying LLM token counts */
  totalOutputTokens: number;
  /** Number of rules that passed */
  rulesPassCount: number;
  /** Number of rules that failed */
  rulesFailCount: number;
  /** Number of rules with error/unknown outcome */
  rulesErrorCount: number;
  /** Total number of rule execution entries */
  totalRuleCount: number;
  /** Number of LLM service calls */
  llmCallCount: number;
}

// ---------------------------------------------------------------------------
// Computation
// ---------------------------------------------------------------------------

/**
 * Compute aggregated performance metrics from a list of audit entries.
 *
 * Returns metrics with totals for duration, tokens, and rule outcomes.
 * If the entries list is empty, returns zeroed metrics with null duration.
 */
export function computeAuditTrailMetrics(entries: AuditEntry[]): AuditTrailMetrics {
  let totalInputTokens = 0;
  let totalOutputTokens = 0;
  let rulesPassCount = 0;
  let rulesFailCount = 0;
  let rulesErrorCount = 0;
  let llmCallCount = 0;

  for (const entry of entries) {
    if (entry.entryType === AuditEntryType.RULE_EXECUTION && entry.description) {
      const parsed = parseRuleExecutionEntry(entry.description);
      if (parsed) {
        switch (parsed.outcome) {
          case 'PASS':
            rulesPassCount++;
            break;
          case 'FAIL':
            rulesFailCount++;
            break;
          default:
            rulesErrorCount++;
            break;
        }
      }
    }

    // Token counts live wherever the backend put them: current trails merge
    // them into the task's own entry (RULE_EXECUTION carrier), while legacy
    // trails have separate EXTERNAL_SERVICE_CALL entries. Each entry is
    // counted at most once, so both formats sum correctly.
    if (
      (entry.entryType === AuditEntryType.RULE_EXECUTION ||
        entry.entryType === AuditEntryType.EXTERNAL_SERVICE_CALL) &&
      entry.description
    ) {
      const parsed = parseExternalServiceEntry(entry.description);
      if (parsed) {
        totalInputTokens += parsed.inputTokens;
        totalOutputTokens += parsed.outputTokens;
        llmCallCount++;
      }
    }
  }

  // Compute total duration from first to last entry timestamp
  let totalDurationMs: number | null = null;
  if (entries.length >= 2) {
    const timestamps = entries
      .map((e) => e.timestamp)
      .filter((t): t is Date => t != null)
      .map((t) => (typeof t === 'string' ? new Date(t).getTime() : t.getTime()));
    if (timestamps.length >= 2) {
      const minTs = Math.min(...timestamps);
      const maxTs = Math.max(...timestamps);
      totalDurationMs = maxTs - minTs;
    }
  }

  const totalRuleCount = rulesPassCount + rulesFailCount + rulesErrorCount;

  return {
    totalDurationMs,
    totalInputTokens,
    totalOutputTokens,
    rulesPassCount,
    rulesFailCount,
    rulesErrorCount,
    totalRuleCount,
    llmCallCount,
  };
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

/**
 * Format a duration in milliseconds to a human-readable string.
 * Returns "--" for null.
 */
export function formatDuration(ms: number | null): string {
  if (ms == null) return '--';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/**
 * Format a token count with thousands separator.
 */
export function formatTokenCount(count: number): string {
  return count.toLocaleString();
}
